import { useEffect, useRef, useState } from "react"

import { getOrCreateSessionId } from "@/features/analytics"
import { useChat } from "@/features/chat"
import {
  getReportById,
  isReportPurchased,
  useUserReports,
  type ReportId,
} from "@/features/today"
import { useWebApp } from "@/features/webapp"

import { getActiveAstrologyEvent } from "../config/astrology-events"
import { getAppNow } from "../lib/get-app-now"
import {
  mightStarsBecomeEligible,
  REPORT_THEME_PRIORITY,
  REPORT_THEME_TO_ID,
  selectAppModal,
} from "../lib/select-app-modal"
import {
  hasShownFree3MinutesGift,
  markFree3MinutesGiftShown,
} from "../persistence/free-3-minutes-gift-seen"
import {
  ensureModalShowState,
  getDailyShowCount,
  recordModalShown,
} from "../persistence/modal-show-state"
import { openAppModal } from "../store/app-modals-store"

const MAX_DAILY_SHOWS = 2
/** Away this long before return counts as a new visit. */
const MIN_AWAY_MS = 3 * 60 * 60 * 1000

/**
 * Auto-launch at most one app modal per visit.
 * A visit is the initial open, or a return after the tab was hidden for at
 * least {@link MIN_AWAY_MS}. Daily cap still limits total shows.
 *
 * Wall-clock (`Date.now`) measures away time — not {@link getAppNow}, which
 * freezes under `?mockDate=` and would never reach MIN_AWAY_MS.
 */
export function useAppModalTriggers() {
  const resolvedRef = useRef(false)
  /** Wall-clock ms when the tab became hidden; null while visible. */
  const hiddenAtMsRef = useRef<number | null>(
    document.visibilityState === "hidden" ? Date.now() : null,
  )
  const [visitEpoch, setVisitEpoch] = useState(0)
  const {
    isStarReportPurchased,
    isTruthsReportPurchased,
    isSoulmateReportPurchased,
    isAstroMapReportPurchased,
    isAuraScanReportPurchased,
  } = useWebApp()
  const { userCreditsCount, userUsedCredits } = useChat()
  const { byType, fetched: reportsFetched } = useUserReports()

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAtMsRef.current = Date.now()
        return
      }
      const hiddenAt = hiddenAtMsRef.current
      hiddenAtMsRef.current = null
      if (hiddenAt === null) return

      const awayMs = Date.now() - hiddenAt
      if (awayMs >= MIN_AWAY_MS) {
        // Long enough away → new visit
        resolvedRef.current = false
        setVisitEpoch((n) => n + 1)
        return
      }
      // Brief switch: don't start a new visit, but resume if we were still
      // waiting (e.g. opened in a background tab).
      if (!resolvedRef.current) {
        setVisitEpoch((n) => n + 1)
      }
    }
    document.addEventListener("visibilitychange", onVisibility)
    return () => document.removeEventListener("visibilitychange", onVisibility)
  }, [])

  useEffect(() => {
    if (resolvedRef.current) return
    // Opened in a background tab — wait until the user focuses it.
    if (document.visibilityState !== "visible") {
      if (hiddenAtMsRef.current === null) {
        hiddenAtMsRef.current = Date.now()
      }
      return
    }

    const now = getAppNow()
    const state = ensureModalShowState(now)

    // First-entry gift: skip if credits were already spent elsewhere (other device).
    if (!hasShownFree3MinutesGift()) {
      if (userUsedCredits === null) return
      if (userUsedCredits > 1) {
        markFree3MinutesGiftShown()
      } else {
        openAppModal("free_3_minutes_gift")
        resolvedRef.current = true
        return
      }
    }

    if (getDailyShowCount(state, now) >= MAX_DAILY_SHOWS) {
      resolvedRef.current = true
      return
    }

    // Soulmate/astromap ownership only comes from GET /v1/reports/me. Selecting a
    // report promo before that list loads treats them as unowned and can lock the
    // session on the wrong modal — wait until the first fetch finishes.
    if (!reportsFetched) return

    const purchasedReportIds = buildPurchasedReportIds(byType, {
      isStarReportPurchased,
      isTruthsReportPurchased,
      isSoulmateReportPurchased,
      isAstroMapReportPurchased,
      isAuraScanReportPurchased,
    })

    const selected = selectAppModal({
      now,
      state,
      credits: userCreditsCount,
      purchasedReportIds,
      hasActiveEvent: !!getActiveAstrologyEvent(now),
    })

    if (selected) {
      openAppModal(selected)
      recordModalShown(selected, getOrCreateSessionId(), now)
      resolvedRef.current = true
      return
    }

    // Wait for credits before concluding nothing to show, if stars might qualify.
    if (userCreditsCount === null && mightStarsBecomeEligible(state, now)) {
      return
    }

    resolvedRef.current = true
  }, [
    userCreditsCount,
    userUsedCredits,
    byType,
    reportsFetched,
    isStarReportPurchased,
    isTruthsReportPurchased,
    isSoulmateReportPurchased,
    isAstroMapReportPurchased,
    isAuraScanReportPurchased,
    visitEpoch,
  ])
}

function buildPurchasedReportIds(
  byType: (id: ReportId) => { status: string } | undefined,
  flags: {
    isStarReportPurchased: boolean
    isTruthsReportPurchased: boolean
    isSoulmateReportPurchased: boolean
    isAstroMapReportPurchased: boolean
    isAuraScanReportPurchased: boolean
  },
): Set<ReportId> {
  const purchased = new Set<ReportId>()

  for (const theme of REPORT_THEME_PRIORITY) {
    const id = REPORT_THEME_TO_ID[theme]
    const report = getReportById(id)
    if (!report) continue

    const api = byType(id)
    if (api) {
      if (api.status !== "purchasable" || isReportPurchased(report, flags)) {
        purchased.add(id)
      }
      continue
    }

    if (isReportPurchased(report, flags)) purchased.add(id)
  }

  return purchased
}
